// Portal yetki modeli v2 — kategori haritası okuyucusu ve eski kolon gölgesi (SAF; spec
// 2026-10-23 §3.1 Y3/Y3a, §4.1). DB'siz; backend `computeClientAccessV2` ve FE çekmecesi aynı
// kuralı buradan okur.
//
// • Harita anahtarı set düğümünün metricId'sidir; her düğüm anahtar olabilir (eski listede yaprak
//   metricId'si de vardı — R2-8). Okuma en yakın ata-ya-da-kendisi girdisidir; girdisi olmayan
//   düğüm gizli (`closed`), NULL harita = tam (`open`).
// • Eski `categories_allowed` dizisi `open` haritasına çevrilir; boş dizi `{}` olur (her şey gizli)
//   — asla NULL (tam) değil (R2-2).
// • Gölge (R2-3): eski kod yeni modelden geniş erişim vermesin diye `categories_allowed` kapalı-
//   başarısız yazılır. Gölgenin DROP'u TODO-KPI-62.
// • Eski kod kayması (R2-3 açığı, Task 2 düzeltme turu 2): 0063 sonrası ESKİ kod da yazabilir
//   (migrate ile yeniden başlatma arası pencere / şemayı koruyan kod geri alımı); yalnız
//   `categories_allowed` + `granted_at = now()` yazar, `category_access_synced_at`'i bilmez. Yeni kod
//   her yazımda `category_access_synced_at = granted_at` yazar → kayma AÇIK DAMGA ile saptanır
//   (`isCategoryAccessDrift`), gölge karşılaştırılmaz. Kayma yoksa eski dizi HİÇ okunmaz; kayma varsa
//   eski dizi yalnız TAVAN olur (`categoryCeilingFromLegacy`) — etkin erişim YALNIZ DARALIR.
import type {
  KpiCategoryAccessMap,
  KpiCategoryLevel,
  KpiEffectiveClientAccess,
  KpiIndicatorOverride,
  KpiSetIndicator,
} from "../schemas/kpi.js";

type TreeNode = Pick<KpiSetIndicator, "id" | "parentId" | "metricId">;

function isCategoryLevel(v: unknown): v is KpiCategoryLevel {
  return v === "open" || v === "readonly";
}

/**
 * `kpi_set_access.category_access` (ya da eski `categories_allowed` dizisi / API gövdesi) okuyucu.
 * Nesne → yalnız `open|readonly` değerli anahtarlar; dizi → `open` haritası (`[]` → `{}`);
 * NULL / jsonb `null` / diğer → NULL (tam).
 */
export function parseCategoryAccess(raw: unknown): KpiCategoryAccessMap | null {
  if (Array.isArray(raw)) {
    const out: KpiCategoryAccessMap = {};
    for (const k of raw) if (typeof k === "string") out[k] = "open";
    return out;
  }
  if (raw !== null && typeof raw === "object") {
    const out: KpiCategoryAccessMap = {};
    for (const [k, v] of Object.entries(raw as Record<string, unknown>)) {
      if (isCategoryLevel(v)) out[k] = v;
    }
    return out;
  }
  return null;
}

/**
 * Tekrarlı sorgu için memolu çözücü: indicatorId → kategori seviyesi. NULL harita → her düğüm
 * `open`; aksi en yakın ata-ya-da-kendisi girdisi, yoksa `closed`. Döngü / bilinmeyen düğüm → `closed`.
 */
export function createCategoryLevelResolver(
  indicators: TreeNode[],
  map: KpiCategoryAccessMap | null,
): (indicatorId: string) => KpiEffectiveClientAccess {
  if (map === null) return () => "open";
  const byId = new Map(indicators.map((i) => [i.id, i]));
  const memo = new Map<string, KpiEffectiveClientAccess>();
  const resolve = (id: string, seen: Set<string>): KpiEffectiveClientAccess => {
    const hit = memo.get(id);
    if (hit !== undefined) return hit;
    const ind = byId.get(id);
    if (!ind || seen.has(id)) return "closed";
    seen.add(id);
    const own = Object.prototype.hasOwnProperty.call(map, ind.metricId)
      ? map[ind.metricId]
      : undefined;
    const out: KpiEffectiveClientAccess =
      own ?? (ind.parentId ? resolve(ind.parentId, seen) : "closed");
    memo.set(id, out);
    return out;
  };
  return (indicatorId) => resolve(indicatorId, new Set());
}

/** Tek düğümün kategori seviyesi (`k`, spec §4.1). */
export function categoryLevelFor(
  indicators: TreeNode[],
  map: KpiCategoryAccessMap | null,
  indicatorId: string,
): KpiEffectiveClientAccess {
  return createCategoryLevelResolver(indicators, map)(indicatorId);
}

/**
 * Eski `categories_allowed` kolonunun kapalı-başarısız gölgesi (Y3a), set ağacına göre. İşletme
 * kapsamı yok ∧ `readonly`/`closed` geçersiz kılma yok ise; aksi `[]`. Harita NULL → NULL (tam).
 * Aksi gölge yalnız TAMAMEN `open` alt ağaçları listeler:
 *   • `open` anahtar, ancak hiçbir (kesin) torununda `readonly` girdisi yoksa — eski kod listedeki
 *     düğümün tüm alt ağacını ata eşleşmesiyle yazılabilir okur; iç içe readonly'yi bilmez;
 *   • `open` istisna metricId'si, ancak düğüm ÇOCUKSUZSA — eski kod onu da torunlara indirirdi;
 *   • ağaçta olmayan anahtar/istisna yazılmaz.
 * Sonuç her zaman yeni modelin dar ya da eşit karşılığıdır (eski kod hiçbir zaman geniş okumaz).
 */
export function legacyCategoriesShadow(
  map: KpiCategoryAccessMap | null,
  entitiesAllowed: readonly string[] | null,
  overrides: readonly KpiIndicatorOverride[],
  tree: readonly TreeNode[],
): string[] | null {
  if (entitiesAllowed !== null) return [];
  if (overrides.some((o) => o.access !== "open")) return [];
  if (map === null) return null;
  const byId = new Map(tree.map((n) => [n.id, n]));
  const byMetric = new Map(tree.map((n) => [n.metricId, n]));
  const parents = new Set(tree.map((n) => n.parentId).filter((p): p is string => !!p));
  // Kesin torununda readonly girdisi olan düğümler (atalar zinciri; döngüye karşı ziyaret kümesi).
  const tainted = new Set<string>();
  for (const [k, v] of Object.entries(map)) {
    if (v !== "readonly") continue;
    const seen = new Set<string>();
    let cur = byMetric.get(k)?.parentId ?? null;
    while (cur && !seen.has(cur)) {
      seen.add(cur);
      tainted.add(cur);
      cur = byId.get(cur)?.parentId ?? null;
    }
  }
  const out: string[] = [];
  const seen = new Set<string>();
  const push = (id: string): void => {
    if (seen.has(id)) return;
    seen.add(id);
    out.push(id);
  };
  for (const [k, v] of Object.entries(map)) {
    const n = byMetric.get(k);
    if (v === "open" && n && !tainted.has(n.id)) push(k);
  }
  for (const o of overrides) {
    const n = byId.get(o.setIndicatorId);
    if (n && !parents.has(n.id)) push(n.metricId);
  }
  return out;
}

/**
 * Eski kod kayması: satırı en son eski kod yazdı mı? Yeni kod `category_access_synced_at =
 * granted_at` yazar; eski kod `granted_at = now()` yazar ve damgayı bilmez → NULL ya da farklı.
 * İstisna CASCADE'i ve gösterge silme `granted_at`'e dokunmaz → normal işlem kayma üretmez.
 */
export function isCategoryAccessDrift(
  grantedAt: Date | string,
  syncedAt: Date | string | null,
): boolean {
  if (syncedAt === null) return true;
  return new Date(syncedAt).getTime() !== new Date(grantedAt).getTime();
}

/**
 * Eski dizinin tavanı (yalnız kaymada): düğüm ya da bir atasının metricId'si listede → `open`,
 * değilse `closed`. Boş liste → her şey `closed` (eski kod "hepsini kaldır").
 */
export interface LegacyCeiling {
  ids: ReadonlySet<string>;
}

/**
 * Kayma yok → NULL (eski dizi yok sayılır; harita olduğu gibi). Kayma var: dizi → liste tavanı
 * (metin olmayan öğeler atılır); SQL NULL / jsonb `null` / undefined → NULL (eski anlam: set
 * varsayılanı, tavan yok); diğer bozuk değer → boş liste (her şey `closed` — kapalı-başarısız).
 */
export function categoryCeilingFromLegacy(legacy: unknown, drift: boolean): LegacyCeiling | null {
  if (!drift) return null;
  if (legacy === null || legacy === undefined) return null;
  if (!Array.isArray(legacy)) return { ids: new Set() };
  return { ids: new Set(legacy.filter((x): x is string => typeof x === "string")) };
}

/**
 * Memolu tavan çözücü: indicatorId → `open` (kendisi ya da atası listede) | `closed`. Tavan NULL →
 * her düğüm `open` (etkisiz). Döngü / bilinmeyen düğüm → `closed`.
 */
export function createLegacyCeilingResolver(
  indicators: TreeNode[],
  ceiling: LegacyCeiling | null,
): (indicatorId: string) => KpiEffectiveClientAccess {
  if (ceiling === null) return () => "open";
  const byId = new Map(indicators.map((i) => [i.id, i]));
  const memo = new Map<string, KpiEffectiveClientAccess>();
  const resolve = (id: string, seen: Set<string>): KpiEffectiveClientAccess => {
    const hit = memo.get(id);
    if (hit !== undefined) return hit;
    const ind = byId.get(id);
    if (!ind || seen.has(id)) return "closed";
    seen.add(id);
    const out: KpiEffectiveClientAccess = ceiling.ids.has(ind.metricId)
      ? "open"
      : ind.parentId
        ? resolve(ind.parentId, seen)
        : "closed";
    memo.set(id, out);
    return out;
  };
  return (indicatorId) => resolve(indicatorId, new Set());
}
